// frontend/src/pages/ResearchNewsFeedPage.js
import React, { useState, useCallback, useEffect, useRef } from 'react';
import RightSidebar from '../components/Dashboard/RightSidebar';
import { getCommonTopics, getArticles } from '../services/api';
import { ChevronDoubleRightIcon, FunnelIcon, NewspaperIcon, XCircleIcon, TagIcon, DocumentTextIcon, AcademicCapIcon, ChatBubbleLeftRightIcon, ExclamationTriangleIcon, InformationCircleIcon, BeakerIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import ArticleCard from '../components/Dashboard/ArticleCard';
import useAuth from '../hooks/useAuth';

const ResearchNewsFeedPage = () => {
  const [filtersFromSidebar, setFiltersFromSidebar] = useState({});
  const [activePageTopic, setActivePageTopic] = useState(null); // For popular topic selection

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [commonTopics, setCommonTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(true);
  
  // Change from grouped articles to flat array
  const [articles, setArticles] = useState([]);
  const [isLoadingFeed, setIsLoadingFeed] = useState(false); 
  const [isLoadingMore, setIsLoadingMore] = useState(false); 
  const [feedError, setFeedError] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreArticles, setHasMoreArticles] = useState(true);
  
  const observer = useRef();
  const feedContainerRef = useRef(null);
  const isFetchingRef = useRef(false);
  const { isAuthenticated } = useAuth();

  const navbarHeight = "88px"; 

  useEffect(() => {
    const fetchTopTopics = async () => {
      setIsLoadingTopics(true);
      try {
        const topics = await getCommonTopics();
        setCommonTopics(topics?.slice(0, 10) || []); // Limit to top 10
      } catch (error) {
        console.error("[Feed] Failed to fetch common topics:", error);
      } finally {
        setIsLoadingTopics(false);
      }
    };
    fetchTopTopics();
  }, []);

  const fetchFeedArticles = useCallback(async (pageNum, combinedFilters, isLoadMore) => {
    if (isFetchingRef.current) {
      console.log("[Feed] Fetch already in progress. Aborting page:", pageNum);
      return;
    }
    isFetchingRef.current = true;

    if (isLoadMore) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingFeed(true);
      setArticles([]); // Clear articles for new feed on page 1 / filter change
    }
    setFeedError('');
    console.log(`[Feed] Fetching page ${pageNum} | LoadMore: ${isLoadMore} | Filters:`, combinedFilters);

    try {
      const articleParams = { ...combinedFilters, page: pageNum, page_size: 12 };
      
      const data = await getArticles(articleParams);
      
      const newArticles = data.results || [];
      setHasMoreArticles(data.next !== null);
      console.log(`[Feed] Page ${pageNum} fetched. HasMore: ${data.next !== null}. New articles: ${newArticles.length}`);

      if (newArticles.length > 0) {
        setArticles(prevArticles => {
          if (isLoadMore) {
            // Add new articles to existing ones
            const existingIds = new Set(prevArticles.map(a => a.id));
            const uniqueNewArticles = newArticles.filter(a => !existingIds.has(a.id));
            return [...prevArticles, ...uniqueNewArticles];
          } else {
            // Replace all articles
            return newArticles;
          }
        });
      } else if (!isLoadMore) { // No results on an initial load
        setArticles([]);
      }
    } catch (err) {
      setFeedError(err.message || 'Failed to fetch articles.');
      if (!isLoadMore) setArticles([]);
    } finally {
      if (isLoadMore) setIsLoadingMore(false);
      else setIsLoadingFeed(false);
      isFetchingRef.current = false;
    }
  }, []); // Removed showSubscribedOnly and isAuthenticated dependencies

  // Effect for initial load and when filters change
  useEffect(() => {
    const combinedFilters = { ...filtersFromSidebar };
    
    console.log("[Feed] Filters changed. Resetting to page 1. New combined filters:", combinedFilters);
    setCurrentPage(1); // Reset to page 1
    setHasMoreArticles(true); // Assume there's more
    fetchFeedArticles(1, combinedFilters, false); // false for isLoadMore
  }, [filtersFromSidebar, activePageTopic, fetchFeedArticles]);

  // IntersectionObserver for infinite scrolling
  const lastArticleElementRef = useCallback(node => {
    if (isFetchingRef.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreArticles && !isFetchingRef.current) {
        setCurrentPage(prevPage => prevPage + 1);
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMoreArticles]); // Only hasMoreArticles needed here, isFetchingRef is checked inside

  // Effect to fetch more articles when currentPage changes (for pagination)
  useEffect(() => {
    if (currentPage > 1 && hasMoreArticles && !isFetchingRef.current) {
      const combinedFilters = { ...filtersFromSidebar };
      fetchFeedArticles(currentPage, combinedFilters, true); // Pass true for isLoadMore
    }
  }, [currentPage, activePageTopic, filtersFromSidebar, hasMoreArticles, fetchFeedArticles]);

  useEffect(() => {
    const handleResize = () => setIsSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const handlePageTopicSelect = (topic) => setActivePageTopic(prev => prev === topic ? null : topic);
  const capitalizeEachWord = (str) => str ? str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : "";

  const totalArticlesCount = articles.length;
  
  const showCentralSpinner = isLoadingFeed && totalArticlesCount === 0;
  const showNoArticlesMessage = !isLoadingFeed && !isFetchingRef.current && totalArticlesCount === 0 && !feedError;
  const showBottomSpinner = isLoadingMore;

  return (
    <div className="flex flex-1 relative bg-slate-100">
      <div 
        ref={feedContainerRef} 
        className={
          `flex-grow p-4 sm:p-6 md:p-8 transition-all duration-300 ease-in-out overflow-y-auto 
          h-[calc(100vh-${navbarHeight})]
          ${isSidebarOpen ? 'lg:mr-[18rem]' : 'mr-0'}`
        }          
        style={{ paddingBlockStart: 'var(--navbar-height)' }}  // Add this line
      >
        <div className="max-w-full mx-auto">
          
          {isLoadingTopics && <div className="text-center p-5"><LoadingSpinner/></div>}

          {showCentralSpinner && <div className="pt-10 flex justify-center"><LoadingSpinner /></div>}
          {feedError && <p className="text-red-600 bg-red-100 p-4 rounded-lg text-center my-6 shadow-md">{feedError}</p>}
          {showNoArticlesMessage && (
            <div className="text-center py-16 bg-white p-6 rounded-xl shadow-lg border-gray-200">
              <NewspaperIcon className="w-20 h-20 text-gray-300 mx-auto mb-6" />
              <p className="text-2xl text-gray-500 mb-3">No articles found.</p>
              <p className="text-gray-400">Try adjusting your filters or check back later.</p>
            </div>
          )}

          {totalArticlesCount > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((article, articleIndex) => {
                const isLastVisibleArticle = articleIndex === articles.length - 1;
                
                return (
                  <div ref={isLastVisibleArticle ? lastArticleElementRef : null} key={article.id} className="flex">
                    <ArticleCard article={article} expansionState={1} />
                  </div>
                );
              })}
            </div>
          )}
          
          {showBottomSpinner && <div className="text-center py-8"><LoadingSpinner /></div>}
          {!hasMoreArticles && totalArticlesCount > 0 && !isFetchingRef.current && !isLoadingFeed && (
            <p className="text-gray-500 text-center py-12 text-lg">You've reached the end!</p>
          )}
        </div>
      </div>

      <button onClick={toggleSidebar} className={`fixed top-1/2 transform -translate-y-1/2 p-3 bg-blue-600 text-white hover:bg-blue-700 rounded-l-xl z-50 shadow-xl transition-all duration-300 ease-in-out ${isSidebarOpen ? 'right-[18rem]' : 'right-0'}`} style={{marginTop: `calc(${navbarHeight} / -2 + 20px)`}} title={isSidebarOpen ? "Hide Filters" : "Show Filters"}>
        {isSidebarOpen ? <ChevronDoubleRightIcon className="w-6 h-6" /> : <FunnelIcon className="w-6 h-6" />}
      </button>

      <RightSidebar onFilterChange={setFiltersFromSidebar} currentFilters={filtersFromSidebar} isOpen={isSidebarOpen} />
    </div>
  );
};

export default ResearchNewsFeedPage;
